import { useState, useEffect } from 'react';
import { Dialog, DialogContent, Box, Grid, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 0,
    maxWidth: '800px',
    width: '100%',
    maxHeight: '500px',
    margin: '16px',
    position: 'relative',
    padding: 0,
    overflow: 'visible',
    [theme.breakpoints.down('md')]: {
      maxWidth: '400px',
      maxHeight: '600px'
    }
  }
}));

const HeaderImage = styled('div')(({ theme }) => ({
  width: '100%',
  aspectRatio: '1/1',
  background: `url('https://www.drapefit.com/img/holiday-offer.jpg') no-repeat center center`,
  backgroundSize: 'cover',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    height: '100%',
    width: '100%'
  }
}));

const CloseButton = styled(IconButton)({
  position: 'absolute',
  right: '-20px',
  top: '-20px',
  border: '2px solid white',
  color: '#fff',
  zIndex: 9999,
  backgroundColor: '#ff6c00',
  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  '&:hover': {
    backgroundColor: '#ff6c00',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)'
  }
});

const SubscribeButton = styled('button')({
  width: '100%',
  padding: '14px',
  border: 'none',
  borderRadius: '25px',
  background: 'linear-gradient(45deg, #ff6c00, #ff8c00)',
  color: 'white',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  textTransform: 'uppercase',
  marginTop: '20px',
  '&:hover': {
    opacity: 0.9
  }
});

const NoThanksLink = styled('a')({
  display: 'block',
  textAlign: 'center',
  color: '#007bff',
  textDecoration: 'none',
  marginTop: '15px',
  cursor: 'pointer',
  fontSize: '14px',
  '&:hover': {
    textDecoration: 'underline'
  }
});

const EmailInput = styled('input')({
  width: '100%',
  padding: '12px',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
  fontSize: '14px',
  '&:focus': {
    outline: 'none',
    borderColor: '#007bff'
  }
});

const SuccessMessage = styled(Box)({
  color: '#4caf50',
  fontSize: '16px',
  fontWeight: 'bold',
  marginTop: '20px',
  animation: 'fadeIn 0.5s ease-in',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
});

const WelcomePopup = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Check if the popup has been shown before
    const hasSeenPopup = localStorage.getItem('welcomePopupShown');
    // Only show if the value is not 'true'
    if (hasSeenPopup !== 'true') {
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
    // Set the value to 'true' when closing
    localStorage.setItem('welcomePopupShown', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('EMAIL', email);
    formData.append('b_c4d312025b5781525c229b745_0d6810bbe6', '');

    try {
      const response = await fetch(
        'https://drapefit.us4.list-manage.com/subscribe/post-json?u=c4d312025b5781525c229b745&id=0d6810bbe6&c=?',
        {
          method: 'POST',
          body: formData,
          mode: 'no-cors'
        }
      );

      setShowSuccess(true);
      // Set the value to 'true' when successfully subscribed
      localStorage.setItem('welcomePopupShown', 'true');

      setTimeout(() => {
        setOpen(false);
      }, 2000);
    } catch (error) {
      console.error('Subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render anything if the popup has been shown before
  if (localStorage.getItem('welcomePopupShown') === 'true') {
    return null;
  }

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <CloseButton onClick={handleClose}>
        <CloseIcon />
      </CloseButton>
      <DialogContent sx={{ padding: 0, height: '100%' }}>
        <Grid container sx={{ height: '100%' }}>
          <Grid item xs={12} md={6} sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <HeaderImage />
          </Grid>
          <Grid item xs={12} md={6} sx={{ order: { xs: 0, md: 1 } }}>
            <Box
              sx={{
                padding: '20px',
                textAlign: 'center',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Box
                sx={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  mb: 2,
                  color: '#232f3e'
                }}
              >
                SUBSCRIBE & SAVE!
              </Box>

              {!showSuccess ? (
                <>
                  <Box
                    sx={{
                      fontSize: '16px',
                      color: '#666',
                      mb: 3
                    }}
                  >
                    Enter your email for 25% off your FIT Box.
                  </Box>

                  <form onSubmit={handleSubmit}>
                    <div id="mc_embed_signup_scroll">
                      <EmailInput
                        type="email"
                        name="EMAIL"
                        className="required email"
                        id="mce-EMAIL"
                        placeholder="Email Address *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isSubmitting}
                      />

                      <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                        <input type="text" name="b_c4d312025b5781525c229b745_0d6810bbe6" tabIndex="-1" value="" />
                      </div>

                      <SubscribeButton
                        type="submit"
                        disabled={isSubmitting}
                        sx={{
                          opacity: isSubmitting ? 0.7 : 1,
                          cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                      </SubscribeButton>
                    </div>
                  </form>

                  <Box
                    sx={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      color: '#ff6c00',
                      mt: 2
                    }}
                  >
                    Treat Yourself This Fall!
                  </Box>

                  <NoThanksLink onClick={handleClose}>No Thanks, Continue</NoThanksLink>
                </>
              ) : (
                <SuccessMessage>
                  🎉 Thank you for subscribing! Your 25% discount code will be sent to your email shortly.
                </SuccessMessage>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  );
};

export default WelcomePopup;
